import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import {memo, Suspense} from 'react'
import * as React from 'react'

import BranchFilter from '../../../../containers/BranchFilter'
import OverviewBreadcrumbs from '../../../../containers/OverviewBreadcrumbs'
import {useIsOverviewLinkSelected, useQueryParam} from '../../../../hooks/routes'
import useDocumentTitle from '../../../../hooks/useDocumentTitle'
import {usePermission} from '../../../../hooks/usePermission'
import useStatusSubscription from '../../../../hooks/useStatusSubscription'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import {getProject} from '../../../../selectors'
import {getHasFavoriteProjects} from '../../../../selectors/projectTrees'
import type {ProjectId} from '../../../../types'
import {Permission, ProjectPageTabNamesEnum, ROOT_PROJECT_ID} from '../../../../types'
import BreadcrumbsDropdown from '../../../common/BreadcrumbsDropdown/BreadcrumbsDropdown'
import Description from '../../../common/Description/Description.container'
import EditEntity from '../../../common/EditEntity/EditEntity.container'
import EditProjectSidebar from '../../../common/EditProjectSidebar/EditProjectSidebar'
import EditProjectSidebarDialog from '../../../common/EditProjectSidebar/EditProjectSidebarDialog'
import FavoriteStar from '../../../common/FavoriteStar/FavoriteStar.container'
import HealthItems from '../../../common/HealthItems/HealthItems'
import Plugin from '../../../common/Plugin/Plugin'
import OverviewHeaderIcon from '../../../common/ProjectOrBuildTypeIcon/OverviewHeaderIcon.container'
import SequenceLoader from '../../../common/SequenceLoader/SequenceLoader'

import OverviewArchivedProjectInfo from './OverviewArchivedProjectInfo/OverviewArchivedProjectInfo'
import ProjectCreateSection from './ProjectCreateSection/ProjectCreateSection'

import styles from './ProjectPageHeader.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  projectId: ProjectId
}

const branchFilter = (
  <BranchFilter key="BranchFilter" seqKey="BranchFilter" className={styles.branchFilter} />
)
const healthItems = <HealthItems key="HealthItem" seqKey="HealthItem" />
const icon = <OverviewHeaderIcon withArrow size="M" />

function ProjectPageHeader({className, projectId}: Props) {
  const isOverview = useIsOverviewLinkSelected()
  const isProblemsTab = useQueryParam('projectTab') === ProjectPageTabNamesEnum.PROBLEMS
  const project = useAppSelector(state => getProject(state, projectId))
  const isFavorites = useAppSelector(
    state => (isOverview && getHasFavoriteProjects(state)) || false,
  )

  const title = project?.name
  const projectArchived = Boolean(project?.archived)

  const actualTitle = isFavorites ? 'Favorite Projects' : isOverview ? 'All Projects' : title
  const headingClassNames = classNames(styles.heading, className, {
    [styles.projectArchived]: projectArchived,
  })

  useStatusSubscription({projectId})
  useDocumentTitle(actualTitle != null ? `${actualTitle} — TeamCity` : null)
  const canEditOwnProfile = usePermission(Permission.CHANGE_OWN_PROFILE, ROOT_PROJECT_ID)

  return (
    <div data-test="overview-header" data-project-id={projectId} className={styles.header}>
      <div className={styles.links}>
        <OverviewBreadcrumbs
          className={styles.breadcrumbs}
          linkClassName={styles.breadcrumbsLink}
        />
        <Plugin placeId={PlaceIdList.SAKURA_PROJECT_LINKS} />
        {isOverview ? (
          <Suspense>
            <ProjectCreateSection className={styles.createProject} />
          </Suspense>
        ) : (
          <SequenceLoader className={styles.linksSequenceLoader} loader="">
            <EditEntity seqKey="EditEntity" />
          </SequenceLoader>
        )}
      </div>
      <H1 className={headingClassNames}>
        {!isOverview && <BreadcrumbsDropdown projectId={projectId} icon={icon} />}
        <span className={styles.title}>{actualTitle}</span>
        {!isOverview && !projectArchived && (
          <FavoriteStar className={styles.star} projectId={projectId} />
        )}
      </H1>
      {!isOverview && (
        <>
          <Description lines={3} projectId={projectId} />
          {projectArchived && <OverviewArchivedProjectInfo />}
        </>
      )}
      <SequenceLoader className={styles.subHeader} loader="">
        {!isOverview && !isProblemsTab && branchFilter}
        {!isOverview && healthItems}
        {canEditOwnProfile && !projectArchived && (
          <EditProjectSidebar seqKey="EditProjectSidebar" button projectId={projectId} />
        )}
      </SequenceLoader>
      {!isOverview && canEditOwnProfile && !projectArchived && (
        <EditProjectSidebarDialog projectId={projectId} />
      )}
      <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
    </div>
  )
}
export default memo(ProjectPageHeader)
